import React, { useState, useEffect } from 'react';
import { Box, Typography, Button, useTheme, Paper, Grid, Card, CardContent, CircularProgress, Chip, Switch, FormControlLabel, Alert } from '@mui/material';
import { tokens } from '../../theme';
import { getToken, base_url } from '../../config/config';
import axios from 'axios';
import { 
  InventoryOutlined, 
  LocalShippingOutlined, 
  WarehouseOutlined, 
  WarningAmberOutlined,
  AddCircleOutlineOutlined,
  SyncOutlined,
  List,
  StorageOutlined,
  SwapHorizOutlined,
  BusinessOutlined
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import StockSummaryCard from './components/StockSummaryCard';
import StockMovementsList from './components/StockMovementsList';
import LowStockTable from './components/LowStockTable';

const StockDashboard = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [stockData, setStockData] = useState({
    productCount: 0,
    totalStockValue: 0,
    lowStockCount: 0,
    recentMovements: [],
    lowStockProducts: [],
    odooStatus: { connected: false, lastSync: null }
  });
  const [dataSource, setDataSource] = useState('custom'); // 'custom' or 'odoo'
  const [syncing, setSyncing] = useState(false);
  const isDarkMode = theme.palette.mode === 'dark';

  const fetchDashboardData = async () => {
    setLoading(true);
    try {
      const token = getToken();
      
      // Add data source parameter to API calls
      const sourceParam = dataSource === 'odoo' ? '?source=odoo' : '?source=custom';
      
      // Fetch products
      const productsResponse = await axios.get(`${base_url}/v1/stock/products${sourceParam}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      // Fetch recent movements
      const movementsResponse = await axios.get(`${base_url}/v1/stock/moves${sourceParam}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      // Fetch low stock products
      const lowStockResponse = await axios.get(`${base_url}/v1/stock/low-stock${sourceParam}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      // Fetch Odoo config status
      let odooStatus = { connected: false, lastSync: null };
      try {
        const odooResponse = await axios.get(`${base_url}/v1/stock/odoo/config`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (odooResponse.data.success) {
          odooStatus = {
            connected: odooResponse.data.data.isActive,
            lastSync: odooResponse.data.data.lastSync
          };
        }
      } catch (error) {
        console.log('Odoo not configured', error);
      }
      
      // Calculate total stock value
      const products = productsResponse.data.data || [];
      const totalStockValue = products.reduce((total, product) => {
        return total + (product.stockQuantity * product.purchasePrice);
      }, 0);
      
      // Set dashboard data
      setStockData({
        productCount: products.length,
        totalStockValue,
        lowStockCount: (lowStockResponse.data.data || []).length,
        recentMovements: (movementsResponse.data.data || []).slice(0, 10),
        lowStockProducts: lowStockResponse.data.data || [],
        odooStatus
      });
    } catch (error) {
      console.error('Error fetching stock dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    
    fetchDashboardData();
  }, [dataSource]);
  
  const handleSyncWithOdoo = async () => {
    setSyncing(true);
    try {
      const token = getToken();
      const response = await axios.post(`${base_url}/v1/stock/odoo/sync`, {}, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.data.success) {
        // Refresh dashboard data
        fetchDashboardData();
      }
    } catch (error) {
      console.error('Error syncing with Odoo:', error);
    } finally {
      setSyncing(false);
    }
  };

  const handleSyncToOdoo = async () => {
    setSyncing(true);
    try {
      const token = getToken();
      const response = await axios.post(`${base_url}/v1/stock/odoo/sync-to-odoo`, {}, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.data.success) {
        console.log('Data synced to Odoo successfully');
      }
    } catch (error) {
      console.error('Error syncing to Odoo:', error);
    } finally {
      setSyncing(false);
    }
  };

  const handleDataSourceChange = (event) => {
    const newSource = event.target.checked ? 'odoo' : 'custom';
    setDataSource(newSource);
    
    // If switching to Odoo and not connected, show warning
    if (newSource === 'odoo' && !stockData.odooStatus.connected) {
      console.warn('Odoo not connected. Please configure Odoo connection first.');
    }
    
    // Refetch data based on new source
    fetchDashboardData();
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="80vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box m="20px">
      {/* HEADER */}
      <Box display="flex" justifyContent="space-between" alignItems="center">
        <Box>
          <Typography variant="h2" color={colors.grey[100]} fontWeight="bold" mb="5px">
            Stock Management
          </Typography>
          <Typography variant="h5" color={colors.greenAccent[400]}>
            Inventory and Stock Control Dashboard
          </Typography>
        </Box>
        
        <Box display="flex" gap="10px">
          <Button
            variant="outlined"
            color="secondary"
            startIcon={<BusinessOutlined />}
            onClick={() => navigate('/stock/owners')}
          >
            Stock Owners
          </Button>
          
          <Button
            variant="outlined"
            color="primary"
            startIcon={<SwapHorizOutlined />}
            onClick={() => navigate('/stock/sync')}
          >
            Sync Management
          </Button>
          
          {stockData.odooStatus.connected && (
            <>
              <Button
                variant="contained"
                color="primary"
                startIcon={syncing ? <CircularProgress size={20} /> : <SyncOutlined />}
                onClick={handleSyncWithOdoo}
                disabled={syncing}
              >
                Sync from Odoo
              </Button>
              <Button
                variant="contained"
                color="success"
                startIcon={syncing ? <CircularProgress size={20} /> : <SwapHorizOutlined />}
                onClick={handleSyncToOdoo}
                disabled={syncing}
              >
                Sync to Odoo
              </Button>
            </>
          )}
          
          <Button
            variant="contained"
            color="secondary"
            startIcon={<WarehouseOutlined />}
            onClick={() => navigate('/stock/products')}
          >
            Manage Stock
          </Button>
          
          <Button
            variant="contained"
            color="secondary"
            startIcon={<LocalShippingOutlined />}
            onClick={() => navigate('/stock/movements')}
          >
            Stock Movements
          </Button>
          
          <Button
            variant="contained"
            color="secondary"
            startIcon={<AddCircleOutlineOutlined />}
            onClick={() => navigate('/stock/products/new')}
          >
            Add Product
          </Button>
        </Box>
      </Box>
      
      {/* DATA SOURCE CONTROL & STATUS */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb="20px">
        <Card sx={{ bgcolor: colors.primary[400], p: 2 }}>
          <FormControlLabel
            control={
              <Switch
                checked={dataSource === 'odoo'}
                onChange={handleDataSourceChange}
                color="secondary"
                disabled={!stockData.odooStatus.connected}
              />
            }
            label={
              <Box display="flex" alignItems="center" gap={1}>
                <StorageOutlined />
                <Typography>
                  Data Source: {dataSource === 'odoo' ? 'Odoo ERP' : 'Custom DB'}
                </Typography>
              </Box>
            }
          />
          {dataSource === 'odoo' && !stockData.odooStatus.connected && (
            <Alert severity="warning" sx={{ mt: 1, fontSize: '0.8rem' }}>
              Odoo not connected. Configure connection first.
            </Alert>
          )}
        </Card>
        <Box>
          <Chip 
            icon={stockData.odooStatus.connected ? <SyncOutlined /> : <WarningAmberOutlined />}
            label={stockData.odooStatus.connected 
              ? `Odoo Connected - Last Sync: ${stockData.odooStatus.lastSync 
                  ? new Date(stockData.odooStatus.lastSync).toLocaleString() 
                  : 'Never'}`
              : 'Odoo Not Connected'}
            color={stockData.odooStatus.connected ? "success" : "default"}
            variant="outlined"
            onClick={() => navigate('/stock/odoo-config')}
            sx={{ cursor: 'pointer' }}
          />
        </Box>
      </Box>

      {/* SUMMARY CARDS */}
      <Grid container spacing={2} mb="20px">
        <Grid item xs={12} sm={6} md={4}>
          <StockSummaryCard 
            title="Total Products"
            value={stockData.productCount}
            icon={<InventoryOutlined sx={{ fontSize: 36 }} />}
            color={colors.primary[400]}
          />
        </Grid>
        
        <Grid item xs={12} sm={6} md={4}>
          <StockSummaryCard 
            title="Total Stock Value"
            value={`€${stockData.totalStockValue.toFixed(2)}`}
            icon={<WarehouseOutlined sx={{ fontSize: 36 }} />}
            color={colors.greenAccent[500]}
          />
        </Grid>
        
        <Grid item xs={12} sm={6} md={4}>
          <StockSummaryCard 
            title="Low Stock Items"
            value={stockData.lowStockCount}
            icon={<WarningAmberOutlined sx={{ fontSize: 36 }} />}
            color={stockData.lowStockCount > 0 ? colors.redAccent[500] : colors.grey[700]}
            onClick={() => stockData.lowStockCount > 0 && navigate('/stock/low-stock')}
            clickable={stockData.lowStockCount > 0}
          />
        </Grid>
      </Grid>

      {/* CONTENT SECTIONS */}
      <Grid container spacing={2}>
        {/* RECENT MOVEMENTS */}
        <Grid item xs={12} md={6}>
          <Paper 
            elevation={3} 
            sx={{ 
              p: 2, 
              bgcolor: colors.primary[400],
              height: "100%",
              minHeight: "400px"
            }}
          >
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
              <Typography variant="h4" fontWeight="bold">
                Recent Stock Movements
              </Typography>
              <Button 
                variant="text" 
                endIcon={<List />}
                onClick={() => navigate('/stock/movements')}
              >
                View All
              </Button>
            </Box>
            <StockMovementsList movements={stockData.recentMovements} />
          </Paper>
        </Grid>
        
        {/* LOW STOCK PRODUCTS */}
        <Grid item xs={12} md={6}>
          <Paper 
            elevation={3} 
            sx={{ 
              p: 2, 
              bgcolor: colors.primary[400],
              height: "100%",
              minHeight: "400px"
            }}
          >
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
              <Typography variant="h4" fontWeight="bold">
                Low Stock Products
              </Typography>
              {stockData.lowStockCount > 0 && (
                <Button 
                  variant="text" 
                  endIcon={<List />}
                  onClick={() => navigate('/stock/low-stock')}
                >
                  View All
                </Button>
              )}
            </Box>
            <LowStockTable products={stockData.lowStockProducts} />
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default StockDashboard;